import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  cn,
} from "@dd/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  CalendarCheck,
  Check,
  CheckCheck,
  CheckCircle2,
  ClipboardCheck,
  FileWarning,
  PackageSearch,
  ShieldAlert,
  Undo2,
} from "lucide-react";
import { api, type ActionableNotification } from "../../lib/api.ts";
import { keys } from "../../lib/query.ts";

function iconFor(kind: string) {
  if (kind === "stock_critical") return PackageSearch;
  if (kind === "appointment_unconfirmed") return CalendarCheck;
  if (kind === "encounter_unsigned") return ClipboardCheck;
  if (kind === "result_unreviewed") return FileWarning;
  return ShieldAlert;
}

const categoryLabel = {
  clinical: "Clínica",
  operational: "Operativa",
  commercial: "Comercial",
} as const;

/** Persistent, permission-filtered notification center for actionable work. */
export function Notifications() {
  const queryClient = useQueryClient();
  const { data } = useQuery({
    queryKey: keys.notifications,
    queryFn: api.notifications.list,
    refetchInterval: 60_000,
  });
  const alerts = data?.notifications ?? [];
  const unread = alerts.filter((item) => !item.readAt);
  const urgent = unread.some((item) => item.severity === "critical");

  const transition = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: "read" | "resolve" | "reopen";
    }) => api.notifications.transition(id, action),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.notifications }),
    meta: { feedback: false },
  });
  const markAll = useMutation({
    mutationFn: api.notifications.markAllRead,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: keys.notifications }),
    meta: { feedback: false },
  });

  function open(item: ActionableNotification) {
    if (!item.readAt) transition.mutate({ id: item.id, action: "read" });
    // The destination enforces its own permission again. A stale notification
    // therefore cannot become an authorization shortcut.
    window.location.assign(item.href);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative grid h-9 w-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={
            unread.length
              ? `Notificaciones: ${unread.length} sin leer`
              : "Notificaciones: nada sin leer"
          }
        >
          <Bell className="h-5 w-5" />
          {unread.length > 0 && (
            <span
              className={cn(
                "absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[10px] font-bold text-white",
                urgent ? "bg-danger" : "bg-warning",
              )}
            >
              {unread.length}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="flex max-h-[min(32rem,calc(100dvh-1rem))] w-[22rem] max-w-[calc(100vw-1rem)] flex-col overflow-hidden [&>div]:flex [&>div]:min-h-0 [&>div]:flex-1 [&>div]:flex-col [&>div]:overflow-hidden">
        <DropdownMenuLabel>
          <span className="block text-sm font-semibold text-foreground">
            Centro de notificaciones
          </span>
          <span className="mt-0.5 block font-normal">
            {alerts.length
              ? `${unread.length} sin leer, ${data?.metrics.resolved ?? 0} resueltas`
              : "Nada pendiente por ahora."}
          </span>
          {data?.metrics.averageResolutionMinutes !== null &&
            data?.metrics.averageResolutionMinutes !== undefined && (
              <span className="block font-normal text-muted-foreground">
                Resolución promedio: {data.metrics.averageResolutionMinutes} min
              </span>
            )}
        </DropdownMenuLabel>
        {unread.length > 0 && (
          <DropdownMenuItem
            className="text-primary"
            onSelect={() => {
              if (!markAll.isPending) markAll.mutate();
            }}
          >
            <CheckCheck />
            <span className="font-medium">Marcar todas como leídas</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />

        <div className="min-h-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain pr-1">
          {alerts.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-4 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
              Sin condiciones que requieran atención.
            </div>
          ) : (
            alerts.map((item) => {
              const Icon = iconFor(item.kind);
              return (
                <div
                  key={item.id}
                  className={cn(
                    "group flex items-center rounded-lg transition-colors hover:bg-muted focus-within:bg-muted",
                    item.readAt && "opacity-75",
                    item.resolvedAt && "opacity-50",
                  )}
                >
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-3 py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    onClick={() => open(item)}
                  >
                    <Icon
                      className={cn(
                        "h-4 w-4 shrink-0",
                        item.severity === "critical"
                          ? "text-danger"
                          : item.category === "clinical"
                            ? "text-primary"
                            : "text-warning",
                      )}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="block truncate text-sm font-medium text-foreground">
                          {item.title}
                        </span>
                        <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
                          {categoryLabel[item.category]}
                        </span>
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {item.detail}
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    disabled={transition.isPending}
                    aria-label={
                      item.resolvedAt
                        ? `Reabrir ${item.title}`
                        : `Resolver ${item.title}`
                    }
                    title={item.resolvedAt ? "Reabrir" : "Marcar resuelta"}
                    className="mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-background hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
                    onClick={() =>
                      transition.mutate({
                        id: item.id,
                        action: item.resolvedAt ? "reopen" : "resolve",
                      })
                    }
                  >
                    {item.resolvedAt ? (
                      <Undo2 className="h-4 w-4" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
